/*
 * The Foozie: the "What should be next?" poll (session A5c, 2026-09-24).
 *
 * One tap records a vote with Klaviyo's CLIENT Events API. No app, and no
 * second copy of klaviyo.js: the vote is one fetch to a.klaviyo.com with the
 * store's PUBLIC key (the "site ID", which klaviyo.js itself ships to every
 * browser). A private key never comes near this file.
 *
 * WHY AN ANONYMOUS ID AND NOT klaviyo.track: klaviyo.track only records a
 * visitor Klaviyo has already identified, so almost every shopper's vote would
 * vanish. The Client Events API accepts `anonymous_id` as the profile's only
 * identifier, so a random id kept in localStorage makes every vote count, and
 * `unique_id` = poll + id makes Klaviyo keep the FIRST vote per browser and
 * drop the rest (Klaviyo's rule: a repeated unique_id for the same profile and
 * metric is recorded once).
 *
 * CONSENT: the vote stores an identifier and sends it to a marketing platform,
 * so it needs both analytics AND marketing consent from Shopify's Customer
 * Privacy API. Outside the UK and EU the API answers true with no banner, so
 * this is one rule for every market, not a market list. The market list
 * (GATED_MARKETS) is only the fallback for when the API never loads: gb and eu
 * then fail CLOSED, everyone else open.
 *
 * THE VOTE IS OPTIMISTIC. The tap shows the results at once and the request
 * goes afterwards, so the interaction costs a few class changes (INP) and a
 * slow network costs nothing. A request that fails for a network reason is
 * queued in localStorage and retried on the next page load; a 4xx is dropped,
 * because retrying a request Klaviyo refused only repeats the refusal.
 *
 * "OTHER" (Luke, L-96, 2026-10-08: "on every poll their should be an 'other'
 * option where the user can enter their own suggestion"). The block adds an
 * Other row to every poll. Tapping it opens a small text box instead of voting;
 * sending the box casts the vote, option_key "other", with the shopper's words
 * as other_text in the SAME event, so the one-vote-per-browser rule is
 * unchanged. The words are cleaned (cleanOtherText) and capped, and they are
 * never shown again: they go to Klaviyo and nowhere on the page.
 *
 * THE EMAIL BOX appears only after a vote (or on a closed poll). Its checkbox
 * is UNTICKED and submitting without ticking it asks for the tick; nothing is
 * pre-consented. The list is single opt-in (Luke, 2026-09-24, L-15).
 *
 * The top of this file is pure functions with no DOM and no imports, so
 * bin/test-fz-poll.mjs can import it under Node. The custom element is defined
 * only in a browser, from Horizon's Component (imported dynamically, so Node
 * never has to resolve '@theme/component').
 */

export const KLAVIYO_CLIENT = "https://a.klaviyo.com/client";
/** The API revision the request shapes were checked against (Klaviyo's OpenAPI spec, 2026-09-24). */
export const KLAVIYO_REVISION = "2026-07-15";
export const DEFAULT_METRIC = "Voted in Poll";
/** Votes from a test run land here, never in the metric the daily tally counts. */
export const TEST_METRIC = "TEST Voted in Poll (A5c)";
export const GATED_MARKETS = ["gb", "eu"];
export const STORAGE = {
  anonymousId: "fz_poll_aid",
  votes: "fz_poll_votes",
  pending: "fz_poll_pending",
  notified: "fz_poll_notified",
  test: "fz_poll_test",
};
const MAX_PENDING = 10;
/** How long to wait for Shopify's privacy API before falling back to the market rule. */
const CONSENT_TIMEOUT_MS = 3000;
const ID_PATTERN = /^[A-Za-z0-9-]{8,64}$/;
/** The Other row's key, the same in the block, here and in the daily tally. */
export const OTHER_KEY = "other";
/** The longest idea kept, in characters (the text box says the same with maxlength). */
export const OTHER_MAX = 80;
/* C0 and C1 controls, plus the zero-width and bidi characters that make text lie about itself. */
const INVISIBLE = /[\u0000-\u001f\u007f-\u009f\u200b-\u200f\u202a-\u202e\u2060-\u2064\ufeff]/g;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/* ---------------------------------------------------------------- storage */

/** localStorage throws in some in-app browsers and private windows; a poll must not. */
export function readJson(storage, key, fallback) {
  try {
    const raw = storage?.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

export function writeJson(storage, key, value) {
  try {
    storage?.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

/** A random id for a browser that has none. */
export function newId(cryptoImpl = globalThis.crypto) {
  if (cryptoImpl?.randomUUID) return `fz-${cryptoImpl.randomUUID()}`;
  const bytes = new Uint8Array(16);
  cryptoImpl.getRandomValues(bytes);
  return `fz-${Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("")}`;
}

/**
 * The browser's anonymous voter id: read it, or mint and keep one. Called only
 * once consent allows it, so a declined visitor never gets an identifier.
 */
export function anonymousId(storage, mint = newId) {
  let id;
  try {
    id = storage?.getItem(STORAGE.anonymousId);
  } catch {
    id = null;
  }
  if (typeof id === "string" && ID_PATTERN.test(id)) return id;
  id = mint();
  try {
    storage?.setItem(STORAGE.anonymousId, id);
  } catch {
    /* Unstorable: this page load still votes, the next one mints again. */
  }
  return id;
}

/* ---------------------------------------------------------------- consent */

/**
 * 'allowed' or 'blocked'. `privacy` is window.Shopify.customerPrivacy, or
 * undefined when it never loaded.
 */
export function consentState(privacy, market, gated = GATED_MARKETS) {
  if (
    privacy &&
    typeof privacy.analyticsProcessingAllowed === "function" &&
    typeof privacy.marketingAllowed === "function"
  ) {
    try {
      return privacy.analyticsProcessingAllowed() && privacy.marketingAllowed() ? "allowed" : "blocked";
    } catch {
      /* fall through to the market rule */
    }
  }
  return gated.includes(market) ? "blocked" : "allowed";
}

/* ------------------------------------------------------------- other text */

/**
 * The shopper's own idea, made safe to send: whitespace collapsed and trimmed,
 * invisible and control characters removed, and capped at OTHER_MAX characters
 * (counted as characters, so a two-part character such as 🍩 is never cut in half). Markup is left as
 * literal text: it is never parsed here and never rendered anywhere.
 */
export function cleanOtherText(value, max = OTHER_MAX) {
  if (typeof value !== "string") return "";
  const text = value.replace(/\s+/g, " ").replace(INVISIBLE, "").replace(/\s+/g, " ").trim();
  return Array.from(text).slice(0, max).join("").trim();
}

/* ------------------------------------------------------- Klaviyo requests */

function headers() {
  return {
    "content-type": "application/vnd.api+json",
    accept: "application/vnd.api+json",
    revision: KLAVIYO_REVISION,
  };
}

function clientUrl(path, companyId) {
  if (!companyId) throw new TypeError("fz-poll: a Klaviyo public key (company_id) is required");
  return `${KLAVIYO_CLIENT}/${path}?company_id=${encodeURIComponent(companyId)}`;
}

/**
 * POST /client/events, as Klaviyo's revision 2026-07-15 spec defines it
 * (EventCreateQueryV2): properties, metric and profile are required; the
 * profile is identified by anonymous_id alone.
 */
export function buildVoteRequest({
  companyId,
  metric = DEFAULT_METRIC,
  anonymousId: aid,
  poll,
  option,
  optionKey,
  product = "",
  market = "",
  themeArm = "variant",
  test = false,
  votedAt,
  otherText,
}) {
  for (const [name, value] of Object.entries({ anonymousId: aid, poll, option, optionKey, metric })) {
    if (!value) throw new TypeError(`fz-poll: ${name} is required to record a vote`);
  }
  const other = optionKey === OTHER_KEY ? cleanOtherText(otherText) : "";
  if (optionKey === OTHER_KEY && !other) throw new TypeError("fz-poll: an Other vote needs the shopper's idea");
  const attributes = {
    properties: {
      poll,
      option,
      option_key: optionKey,
      product,
      market,
      theme_arm: themeArm,
      source: "fz-poll",
      test: Boolean(test),
      ...(other ? { other_text: other } : {}),
    },
    metric: { data: { type: "metric", attributes: { name: metric } } },
    profile: { data: { type: "profile", attributes: { anonymous_id: aid } } },
    unique_id: `${poll}:${aid}`,
  };
  if (votedAt) attributes.time = new Date(votedAt).toISOString();
  return {
    url: clientUrl("events", companyId),
    init: { method: "POST", headers: headers(), body: JSON.stringify({ data: { type: "event", attributes } }) },
  };
}

/**
 * POST /client/subscriptions (OnsiteSubscriptionCreateQuery). This schema has
 * no anonymous_id, so the vote reaches the email profile as properties: the
 * poll and the option chosen, as agreed with the orchestrator.
 */
export function buildSubscribeRequest({ companyId, listId, email, poll, option = "", market = "" }) {
  if (!listId) throw new TypeError("fz-poll: a Klaviyo list id is required to subscribe");
  if (!isValidEmail(email)) throw new TypeError("fz-poll: a valid email is required to subscribe");
  if (!poll) throw new TypeError("fz-poll: poll is required to subscribe");
  const properties = { fz_poll_last: poll };
  if (option) properties[`fz_poll_${poll}`] = option;
  if (market) properties.fz_poll_market = market;
  const body = {
    data: {
      type: "subscription",
      attributes: {
        custom_source: `fz-poll: ${poll}`,
        profile: {
          data: {
            type: "profile",
            attributes: {
              email: email.trim(),
              properties,
              subscriptions: { email: { marketing: { consent: "SUBSCRIBED" } } },
            },
          },
        },
      },
      relationships: { list: { data: { type: "list", id: listId } } },
    },
  };
  return {
    url: clientUrl("subscriptions", companyId),
    init: { method: "POST", headers: headers(), body: JSON.stringify(body) },
  };
}

export function isValidEmail(value) {
  return typeof value === "string" && EMAIL_PATTERN.test(value.trim());
}

/**
 * What a response means. 202 is Klaviyo's only success. A network failure or a
 * 429/5xx is worth retrying later; any other status is a refusal to drop.
 */
export function outcome(status) {
  if (status === 202 || status === 200) return "ok";
  if (status === 0 || status === 429 || status >= 500) return "retry";
  return "drop";
}

/* ---------------------------------------------------------------- results */

/**
 * The rows the results view shows. `options` carry the daily tally's counts;
 * the visitor's own vote is added unless the tally already includes it (it
 * does once the tally ran after the vote). Percentages use largest-remainder
 * rounding, so they always add up to exactly 100.
 */
export function resultRows(options, { pickedKey = null, votedAt = 0, tallyUpdatedAt = 0 } = {}) {
  const own = pickedKey && !(tallyUpdatedAt && votedAt && votedAt <= tallyUpdatedAt) ? 1 : 0;
  const rows = options.map((o) => ({
    key: o.key,
    label: o.label,
    count: Math.max(0, Number(o.count) || 0) + (o.key === pickedKey ? own : 0),
    picked: o.key === pickedKey,
  }));
  const total = rows.reduce((sum, r) => sum + r.count, 0);
  const counted = total - own;
  if (!total) return { rows: rows.map((r) => ({ ...r, pct: 0, share: 0 })), total, counted };

  const exact = rows.map((r) => (r.count * 100) / total);
  const floors = exact.map(Math.floor);
  let left = 100 - floors.reduce((a, b) => a + b, 0);
  const order = exact.map((e, i) => [e - floors[i], i]).sort((a, b) => b[0] - a[0] || a[1] - b[1]);
  for (const [, i] of order) {
    if (left <= 0) break;
    floors[i] += 1;
    left -= 1;
  }
  return {
    rows: rows.map((r, i) => ({ ...r, pct: floors[i], share: r.count / total })),
    total,
    counted,
  };
}

/**
 * Shopify's `t` filter HTML-escapes a translation (so "You're" arrives as
 * "You&#39;re"), and the block passes its strings through `| t | json`. They
 * are written with textContent, which would show the entity literally, so the
 * five entities that escape produces are turned back first. Nothing else is
 * decoded: this is not an HTML parser, and a string is never set as HTML.
 */
const ENTITIES = { "&amp;": "&", "&lt;": "<", "&gt;": ">", "&quot;": '"', "&#39;": "'", "&#x27;": "'" };
export function decodeText(value) {
  return typeof value === "string" ? value.replace(/&(?:amp|lt|gt|quot|#39|#x27);/g, (e) => ENTITIES[e]) : value;
}

/** English plural, from the two strings the block renders from its locale. */
export function plural(count, one, other) {
  return (count === 1 ? one : other).replace("[count]", String(count));
}

/* ------------------------------------------------------ the custom element */

if (typeof window !== "undefined" && window.customElements && !customElements.get("fz-poll-component")) {
  import("@theme/component").then(({ Component }) => {
    if (customElements.get("fz-poll-component")) return;
    customElements.define("fz-poll-component", definePoll(Component));
  });
}

function definePoll(Component) {
  return class FzPollComponent extends Component {
    /** @type {any} */
    config = {};

    connectedCallback() {
      super.connectedCallback();
      try {
        this.config = JSON.parse(this.refs.config?.textContent || "{}");
        this.config.strings = Object.fromEntries(
          Object.entries(this.config.strings ?? {}).map(([k, v]) => [k, decodeText(v)]),
        );
      } catch {
        return;
      }
      this.storage = safeStorage();
      this.test = isTestRun();
      const vote = readJson(this.storage, STORAGE.votes, {})[this.config.poll];

      if (vote && this.#hasOption(vote.key)) {
        this.#showResults(vote.key, vote.at, false);
      } else if (this.config.state === "closed") {
        this.#showResults(null, 0, false);
      } else {
        this.#watchConsent();
      }
      if (readJson(this.storage, STORAGE.notified, {})[this.config.poll]) this.#markNotified();
      this.#flushPending();
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      document.removeEventListener("visitorConsentCollected", this.#onConsent);
    }

    /** on:click on an option. */
    vote(event) {
      const button = event.target.closest?.("[data-key]") ?? event.target;
      if (!button?.dataset?.key) return;
      const state = this.dataset.state;
      if (state === "checking") {
        // Shopify's privacy API is still loading: hold the tap, decide when it answers.
        this.#heldTap = button;
        return;
      }
      if (state === "blocked") return this.#nudgeConsent();
      if (state !== "vote" || this.#voting) return;
      if (button.dataset.key === OTHER_KEY) return this.#openOther(button);
      this.#castVote(button);
    }

    /** on:submit on the Other box: the shopper's own idea becomes the vote. */
    suggest(event) {
      event.preventDefault();
      const { otherText, otherError } = this.refs;
      const text = cleanOtherText(otherText?.value ?? "");
      if (!text) {
        if (otherError) {
          otherError.textContent = this.config.strings?.other_empty ?? "";
          otherError.hidden = false;
        }
        otherText?.focus?.();
        return;
      }
      if (otherError) otherError.hidden = true;
      const button = this.#buttons().find((b) => b.dataset.key === OTHER_KEY);
      if (button && this.dataset.state === "vote" && !this.#voting) this.#castVote(button, text);
    }

    /** on:click on the consent note's button: Shopify's own preferences dialog. */
    openCookieSettings() {
      window.privacyBanner?.showPreferences?.();
    }

    /** on:submit on the email form. */
    async subscribe(event) {
      event.preventDefault();
      const { email, consentBox, submit } = this.refs;
      const strings = this.config.strings ?? {};
      const value = email?.value ?? "";

      if (!isValidEmail(value)) return this.#formError(strings.notify_invalid, email);
      if (!consentBox?.checked) return this.#formError(strings.notify_need_consent, consentBox);
      this.#formError("");

      let request;
      try {
        request = buildSubscribeRequest({
          companyId: this.config.companyId,
          listId: this.config.listId,
          email: value,
          poll: this.config.poll,
          option: this.#pickedLabel(),
          market: this.config.market,
        });
      } catch (error) {
        console.warn(error);
        return this.#formError(strings.notify_error, submit);
      }
      if (this.config.designMode) return;

      submit?.setAttribute("aria-busy", "true");
      submit?.setAttribute("aria-disabled", "true");
      if (submit) submit.disabled = true;
      const status = await send(request);
      submit?.removeAttribute("aria-busy");
      submit?.removeAttribute("aria-disabled");
      if (submit) submit.disabled = false;

      if (outcome(status) !== "ok") return this.#formError(strings.notify_error, submit);
      const notified = readJson(this.storage, STORAGE.notified, {});
      notified[this.config.poll] = Date.now();
      writeJson(this.storage, STORAGE.notified, notified);
      this.#markNotified(true);
    }

    /* ------------------------------------------------------------ private */

    #openOther(button) {
      const { other, otherText } = this.refs;
      if (!other) return;
      button.setAttribute("aria-expanded", "true");
      other.hidden = false;
      otherText?.focus?.({ preventScroll: false });
    }

    #castVote(button, otherText) {
      const key = button.dataset.key;
      const option = button.dataset.label || key;
      const at = Date.now();
      // The tap's own frame shows only the pick (its border and tick). The
      // bars, the text, storage and the request follow in the NEXT frame, so
      // the paint that INP measures stays small: measured on f1 at 4x CPU the
      // whole re-render in one frame cost 159 ms of paint on desktop.
      this.#voting = true;
      this.dataset.animate = "";
      button.setAttribute("data-picked", "");
      requestAnimationFrame(() => setTimeout(() => this.#recordVote(key, option, at, otherText)));
    }

    #recordVote(key, option, at, otherText) {
      this.#showResults(key, at, true);
      this.#announce(this.config.strings?.thanks);

      const votes = readJson(this.storage, STORAGE.votes, {});
      votes[this.config.poll] = { key, at };
      writeJson(this.storage, STORAGE.votes, votes);

      if (this.config.designMode) return;
      let request;
      try {
        request = buildVoteRequest({
          companyId: this.config.companyId,
          metric: this.test ? TEST_METRIC : this.config.metric,
          anonymousId: anonymousId(this.storage),
          poll: this.config.poll,
          option,
          optionKey: key,
          product: this.config.product,
          market: this.config.market,
          test: this.test,
          votedAt: at,
          otherText,
        });
      } catch (error) {
        console.warn(error);
        return;
      }
      this.#queue(request);
      this.#send(request);
    }

    /** @type {HTMLElement | null} */
    #heldTap = null;
    #voting = false;
    /** @type {Animation | undefined} */
    #nudge;
    #flushed = false;

    #hasOption(key) {
      return (this.config.options ?? []).some((o) => o.key === key);
    }

    #pickedLabel() {
      const key = this.dataset.voted;
      return (this.config.options ?? []).find((o) => o.key === key)?.label ?? "";
    }

    #onConsent = () => {
      if (["vote", "blocked", "checking"].includes(this.dataset.state ?? "")) this.#applyConsent();
    };

    #watchConsent() {
      document.addEventListener("visitorConsentCollected", this.#onConsent);
      const shopify = /** @type {any} */ (window).Shopify;
      if (shopify?.customerPrivacy || typeof shopify?.loadFeatures !== "function") return this.#applyConsent();

      // Not loaded yet. Deciding now would show a consenting UK shopper the
      // "accept cookies" note for a moment, so wait (the options still look
      // and press as normal), and fall back to the market rule if it never comes.
      this.#setState("checking");
      let settled = false;
      const settle = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        this.#applyConsent();
      };
      const timer = setTimeout(settle, CONSENT_TIMEOUT_MS);
      shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], settle);
    }

    #applyConsent() {
      const state = consentState(/** @type {any} */ (window).Shopify?.customerPrivacy, this.config.market);
      this.#setState(state === "allowed" ? "vote" : "blocked");
      const { consent, cookieButton } = this.refs;
      if (consent) consent.hidden = state === "allowed";
      if (cookieButton) cookieButton.hidden = typeof window.privacyBanner?.showPreferences !== "function";
      for (const button of this.#buttons()) {
        if (state === "allowed") button.removeAttribute("aria-disabled");
        else button.setAttribute("aria-disabled", "true");
      }
      const held = this.#heldTap;
      this.#heldTap = null;
      if (state === "allowed") {
        this.#flushPending();
        // A held tap on Other opens its box: an Other vote is never cast without the shopper's words.
        if (held) held.dataset.key === OTHER_KEY ? this.#openOther(held) : this.#castVote(held);
      } else if (held) {
        this.#nudgeConsent();
      }
    }

    #nudgeConsent() {
      const { consent, cookieButton } = this.refs;
      if (!consent) return;
      consent.hidden = false;
      const still = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
      // A second tap restarts the nudge cleanly instead of stacking a second shake on the first.
      this.#nudge?.cancel();
      this.#nudge = consent.animate?.(
        still
          ? [{ opacity: 0.6 }, { opacity: 1 }]
          : [
              { transform: "translateX(0)" },
              { transform: "translateX(-4px)" },
              { transform: "translateX(4px)" },
              { transform: "translateX(-2px)" },
              { transform: "translateX(0)" },
            ],
        { duration: still ? 160 : 240, easing: "cubic-bezier(0.23, 1, 0.32, 1)" },
      );
      (cookieButton && !cookieButton.hidden ? cookieButton : consent).focus?.({ preventScroll: false });
    }

    #buttons() {
      const refs = this.refs.options;
      return Array.isArray(refs) ? refs : refs ? [refs] : [];
    }

    #setState(state) {
      this.dataset.state = state;
    }

    /**
     * Turns every option into its result row IN PLACE: the same buttons, now
     * with a fill behind the label and a percentage at the end, so nothing
     * moves and the reveal reads as the vote landing.
     */
    #showResults(pickedKey, votedAt, animate) {
      const counts = new Map((this.config.options ?? []).map((o) => [o.key, o]));
      const { rows, total, counted } = resultRows(this.config.options ?? [], {
        pickedKey,
        votedAt,
        tallyUpdatedAt: this.config.tallyUpdatedAt || 0,
      });
      const byKey = new Map(rows.map((r) => [r.key, r]));
      const strings = this.config.strings ?? {};

      if (animate) this.dataset.animate = "";
      else delete this.dataset.animate;

      this.#buttons().forEach((button, index) => {
        const row = byKey.get(button.dataset.key);
        if (!row || !counts.has(button.dataset.key)) return;
        button.style.setProperty("--fz-poll-share", String(row.share));
        button.style.setProperty("--fz-poll-i", String(index));
        button.setAttribute("aria-disabled", "true");
        button.removeAttribute("aria-pressed");
        button.toggleAttribute("data-picked", row.picked);
        const pct = button.querySelector(".fz-poll__pct");
        if (pct) pct.textContent = counted ? `${row.pct}%` : "";
        const sr = button.querySelector(".fz-poll__sr");
        if (sr) {
          const parts = [counted ? `${row.pct}%` : "", row.picked ? strings.your_vote : ""].filter(Boolean);
          sr.textContent = parts.length ? `, ${parts.join(", ")}` : "";
        }
      });

      if (pickedKey) this.dataset.voted = pickedKey;
      this.toggleAttribute("data-first", !counted);
      const { summary, consent, notify } = this.refs;
      if (summary) {
        const tally = counted
          ? plural(total, strings.total_one ?? "[count] vote so far", strings.total_other ?? "[count] votes so far")
          : pickedKey
            ? (strings.first ?? "")
            : "";
        summary.textContent =
          this.config.state === "closed" ? [strings.closed, tally].filter(Boolean).join(" ") : tally;
      }
      if (consent) consent.hidden = true;
      if (this.refs.other) this.refs.other.hidden = true;
      if (notify && !this.hasAttribute("data-notified")) {
        // After a tap, the email box waits for the frame after the results:
        // the tap's own frame then paints only the bars (INP), and the box's
        // entrance follows them, as the eye does.
        if (animate)
          requestAnimationFrame(() => setTimeout(() => (notify.hidden = this.hasAttribute("data-notified"))));
        else notify.hidden = false;
      }
      this.#setState(this.config.state === "closed" && !pickedKey ? "closed" : "results");
      this.dataset.results = "";
    }

    #announce(text) {
      if (this.refs.status && text) this.refs.status.textContent = text;
    }

    #formError(text, focusTarget) {
      const { error } = this.refs;
      if (!error) return;
      error.textContent = text || "";
      error.hidden = !text;
      if (text) focusTarget?.focus?.();
    }

    #markNotified(justNow = false) {
      this.setAttribute("data-notified", "");
      const { notify, notified } = this.refs;
      if (notify) notify.hidden = true;
      if (notified) {
        notified.hidden = false;
        if (justNow) this.#announce(this.config.strings?.notify_success);
      }
    }

    #queue(request) {
      const pending = readJson(this.storage, STORAGE.pending, []).filter((p) => p.body !== request.init.body);
      pending.push({ url: request.url, body: request.init.body });
      writeJson(this.storage, STORAGE.pending, pending.slice(-MAX_PENDING));
    }

    #dequeue(body) {
      const pending = readJson(this.storage, STORAGE.pending, []);
      writeJson(
        this.storage,
        STORAGE.pending,
        pending.filter((p) => p.body !== body),
      );
    }

    async #send(request) {
      const result = outcome(await send(request));
      if (result !== "retry") this.#dequeue(request.init.body);
      if (result === "drop") console.warn("fz-poll: Klaviyo refused the vote");
    }

    /** Retries votes a closed tab or a dropped connection never delivered. */
    #flushPending() {
      if (this.#flushed || this.config.designMode) return;
      if (consentState(/** @type {any} */ (window).Shopify?.customerPrivacy, this.config.market) !== "allowed") return;
      this.#flushed = true;
      const pending = readJson(this.storage, STORAGE.pending, []);
      for (const item of pending) {
        if (!item?.url?.startsWith(`${KLAVIYO_CLIENT}/events?`)) continue;
        this.#send({ url: item.url, init: { method: "POST", headers: headers(), body: item.body } });
      }
    }
  };
}

async function send(request) {
  try {
    const response = await fetch(request.url, request.init);
    return response.status;
  } catch {
    return 0;
  }
}

function safeStorage() {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

/**
 * A test run is opted into with ?fz_poll_test=1 and lasts the tab's session.
 * Its votes go to TEST_METRIC, which the daily tally never reads.
 */
function isTestRun() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("fz_poll_test") === "1") window.sessionStorage.setItem(STORAGE.test, "1");
    if (params.get("fz_poll_test") === "0") window.sessionStorage.removeItem(STORAGE.test);
    return window.sessionStorage.getItem(STORAGE.test) === "1";
  } catch {
    return false;
  }
}
